/**
 * PostHog error grouping for the legacy editor (studio.framky.com).
 *
 * SOURCE OF TRUTH: framky-frontend/src/lib/analytics/posthog-error-tracking.ts
 * Keep this in sync with that file. There is no shared package between the two
 * apps, so this is a deliberate copy. The legacy app additionally drops raw
 * `.heic` texture loads (Safari can't decode them into a Three.js texture).
 *
 * Used as the PostHog `before_send` hook in src/index.jsx.
 */

/** Replace dynamic path segments (ids, hashes) so endpoints group together. */
export function normalizeUrlForFingerprint(url) {
  let s = url;
  s = s.split('?')[0].split('#')[0];
  s = s.replace(/[0-9a-f]{8,}/gi, ':id');
  s = s.replace(/\/\d+\//g, '/:id/');
  s = s.replace(/\/\d+$/, '/:id');
  return s;
}

/** Collapse the dynamic parts of a message so the same logical error groups. */
export function normalizeMessage(value) {
  let s = value || '';
  s = s.replace(/\b(?:https?|blob|data|wss?):[^\s'"]+/gi, ':url');
  s = s.replace(/\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, ':uuid');
  s = s.replace(/\b[0-9a-f]{12,}\b/gi, ':hex');
  s = s.replace(/'[^']*'/g, "':q'").replace(/"[^"]*"/g, '":q"');
  s = s.replace(/\d+/g, ':n');
  s = s.replace(/\s+/g, ' ').trim();
  return s.length > 120 ? `${s.slice(0, 120)}…` : s;
}

/** Parse an HTTP/Axios error into {status, method, path}; null if not HTTP. */
export function parseHttpError(exc, props = {}) {
  const value = (exc && exc.value) || '';
  const isHttp =
    props.is_axios_error === true ||
    (exc && exc.type === 'AxiosError') ||
    /\b(?:HTTP|status code|API Error)\s*\d{3}\b/i.test(value);
  if (!isHttp) return null;

  let status = props.response_status;
  if (!status) {
    const m = value.match(/(?:status code|HTTP|API Error)\s*(\d{3})/i);
    if (m) status = Number(m[1]);
  }

  let method = props.request_method;
  if (!method) {
    const m = value.match(/\b(GET|POST|PUT|PATCH|DELETE|HEAD|OPTIONS)\b/);
    if (m) method = m[1];
  }

  let path = props.request_url;
  if (!path) {
    const m = value.match(/\/[^\s?#]*/);
    if (m) path = m[0];
  }

  return {
    status: status != null ? Number(status) : undefined,
    method: method ? String(method).toUpperCase() : undefined,
    path: path ? normalizeUrlForFingerprint(path) : undefined,
  };
}

/** Classify one exception: {action:'drop'} or {action:'fingerprint', fingerprint}. */
export function classifyException(exc, props = {}) {
  const value = (exc && exc.value) || '';
  const type = (exc && exc.type) || 'Error';

  // ---- DROP: not-our-code / transient / already-handled noise ----
  if (value) {
    if (/Request failed with status code 403/.test(value)) return { action: 'drop' };
    // expired signed Spaces/S3 URL OR raw .heic the browser can't decode as a texture
    if (/Could not load https?:\/\/.*([?&]Expires=\d+|\.heic\b)/i.test(value)) return { action: 'drop' };
    if (/Failed to load image: https?:\/\/.*[?&]Expires=\d+/.test(value)) return { action: 'drop' };
    if (/Failed to load image: blob:/.test(value)) return { action: 'drop' };
    if (/getShaderPrecisionFormat/.test(value)) return { action: 'drop' };
    if (/shaderSource must be an instance of WebGLShader/.test(value)) return { action: 'drop' };
    if (/cashbackReminder/.test(value)) return { action: 'drop' };
    if (/No Listener:\s*tabs:/.test(value) || /\btabs:(outgoing|incoming)\.message\./.test(value)) return { action: 'drop' };
    if (/Failed to load Google Identity Services script/.test(value)) return { action: 'drop' };
    if (/Java object is gone/.test(value)) return { action: 'drop' };
    if (
      type === 'DOMException' &&
      /The object can not be found here/.test(value) &&
      exc.stacktrace &&
      exc.stacktrace.frames &&
      exc.stacktrace.frames.some(
        (f) =>
          /PointerCapture/i.test((f && (f.resolved_name || f.mangled_name)) || '') ||
          ((f && f.source) || '').includes('@react-three/fiber')
      )
    ) {
      return { action: 'drop' };
    }
  }

  // ---- FINGERPRINT: HTTP / Axios (status is its own dimension) ----
  const http = parseHttpError(exc, props);
  if (http) {
    if (http.status === 403) return { action: 'drop' };
    const parts = ['AxiosError', http.status != null ? String(http.status) : 'ERR'];
    if (http.path) parts.push(`${http.method || '?'} ${http.path}`);
    return { action: 'fingerprint', fingerprint: parts };
  }

  // ---- FINGERPRINT: build-varying bundles ----
  if (type === 'ChunkLoadError' || /Loading chunk [\w.-]+ failed/.test(value)) {
    return { action: 'fingerprint', fingerprint: ['ChunkLoadError', 'Loading chunk failed'] };
  }

  // ---- CATCH-ALL ----
  return { action: 'fingerprint', fingerprint: value ? [type, normalizeMessage(value)] : [type] };
}

/** PostHog before_send hook: drop noise, else assign a stable fingerprint. */
export function normalizeExceptionFingerprint(event) {
  if (!event || event.event !== '$exception' || !event.properties || !event.properties.$exception_list) {
    return event;
  }

  const exceptions = event.properties.$exception_list;
  let primary = null;
  for (const exc of exceptions) {
    const verdict = classifyException(exc, event.properties);
    if (verdict.action === 'drop') return null;
    if (primary === null) primary = verdict.fingerprint;
  }
  if (primary) event.properties["$exception_fingerprint"] = primary.join(" | ");
  return event;
}
